import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import './VerifMembre.css';

interface MembreData {
  id?: string;
  nom: string;
  prenom: string;
  loge: string;
  grades: string;
  statut: string;
  photo?: string;
}

function VerifMembre() {
  const [membreInfo, setMembreInfo] = useState<MembreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Récupérer le paramètre id de deux façons possibles
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // Utiliser l'ID de l'URL ou de la query string
  const id = params.id || queryParams.get('id');

  useEffect(() => {
    const fetchMembreData = async () => {
      try {
        console.log("ID du membre récupéré:", id); // Log pour débogage
        
        if (!id) {
          setError('Identifiant du membre manquant');
          setLoading(false);
          return;
        }

        // Récupérer les données du membre depuis Firestore
        const db = getFirestore();
        const membreRef = doc(db, 'Membres', id);
        
        console.log("Tentative de récupération du document:", id); // Log pour débogage
        
        const membreDoc = await getDoc(membreRef);

        if (!membreDoc.exists()) {
          console.log("Document non trouvé pour l'ID:", id); // Log pour débogage
          setError('Membre non trouvé');
          setLoading(false);
          return;
        }

        // Récupérer les données du membre
        const membreData = membreDoc.data() as MembreData;
        membreData.id = id;
        
        console.log("Données du membre récupérées:", membreData); // Log pour débogage
        
        // Tout est valide, on affiche les informations
        setMembreInfo(membreData);
        setLoading(false);
      } catch (err: any) {
        console.error('Erreur lors de la vérification du membre:', err);
        setError(`Erreur lors de la vérification des informations du membre: ${err.message}`);
        setLoading(false);
      }
    };

    fetchMembreData();
  }, [id]);

  if (loading) {
    return (
      <div className="verif-membre-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Vérification des informations du membre...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="verif-membre-container">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h2>Vérification échouée</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  if (!membreInfo) {
    return (
      <div className="verif-membre-container">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Aucune information disponible</h2>
          <p>Impossible de récupérer les informations du membre.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="verif-membre-container">
      <div className="verif-card">
        <div className="verif-header">
          <h2>Vérification de membre</h2>
          <div className={`status-badge ${membreInfo.statut === 'Valide' ? 'valid' : 'invalid'}`}>
            {membreInfo.statut}
          </div>
        </div>

        {membreInfo.photo && (
          <div className="photo-container">
            <img src={membreInfo.photo} alt={`${membreInfo.prenom} ${membreInfo.nom}`} className="membre-photo1" />
          </div>
        )}

        <div className="membre-info">
          <div className="info-row">
            <div className="info-label">Nom:</div>
            <div className="info-value">{membreInfo.nom}</div>
          </div>
          
          <div className="info-row">
            <div className="info-label">Prénom:</div>
            <div className="info-value">{membreInfo.prenom}</div>
          </div>
          
          <div className="info-row">
            <div className="info-label">Loge:</div>
            <div className="info-value">{membreInfo.loge}</div>
          </div>
          
          <div className="info-row">
            <div className="info-label">Grade:</div>
            <div className="info-value">{membreInfo.grades}</div>
          </div>
        </div>

        <div className="verification-result">
          <div className="valid-icon">✓</div>
          <p>Ce membre est authentique et ses informations sont validées.</p>
          <p className="verification-date">Vérifié le {new Date().toLocaleDateString()} à {new Date().toLocaleTimeString()}</p>
        </div>
      </div>
    </div>
  );
}

export default VerifMembre;